import { useI18n } from '@/hooks/useI18n';
import { useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { Tile } from '@/components/primitives/Tile';
import { Chip } from '@/components/primitives/Chip';
import { ErrorInline } from '@/components/primitives/ErrorInline';
import { Skeleton } from '@/components/ui/skeleton';
import { useIdentityPerformance } from '@/hooks/queries/useIdentityPerformance';
import { formatNumber, formatPct } from '@/lib/format';
import { cn } from '@/lib/cn';
import { IdentityHealthPopover } from '@/components/identities-view/IdentityHealthPopover';
import { SectionHeader } from './SectionHeader';
import { AnalyticsEmpty } from './AnalyticsEmpty';

interface IdentityPerformanceSectionProps {
  periodDays: number;
}

type BreakdownMetric = 'views' | 'followers';

export function IdentityPerformanceSection({ periodDays }: IdentityPerformanceSectionProps) {
  const { t, locale } = useI18n();
  const [breakdown, setBreakdown] = useState<BreakdownMetric>('views');
  const perf = useIdentityPerformance(periodDays);

  const sorted = useMemo(() => {
    if (!perf.data) return [];
    return [...perf.data].sort((a, b) =>
      breakdown === 'views'
        ? b.totalViewsDelta - a.totalViewsDelta
        : b.totalFollowers - a.totalFollowers,
    );
  }, [perf.data, breakdown]);

  const metricLabel =
    breakdown === 'views'
      ? t('Vues gagnées sur la période', 'Views gained in the period')
      : t('Abonnés au dernier relevé', 'Followers at the latest observation');
  const maxValue =
    sorted.length > 0
      ? breakdown === 'views'
        ? sorted[0].totalViewsDelta
        : sorted[0].totalFollowers
      : 0;

  return (
    <section className="min-w-0 space-y-3">
      <SectionHeader
        title={t('Performance par identité', 'Performance by identity')}
        subtitle={t(
          'Comparez les vues gagnées et l’audience de vos groupes de comptes.',
          'Compare views gained and audience across your account groups.',
        )}
        rightSlot={
          <details className="max-w-sm">
            <summary className="flex min-h-11 cursor-pointer list-none items-center gap-1.5 rounded px-1 text-[12px] text-text-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet [&::-webkit-details-marker]:hidden">
              <Info className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {t('Comprendre le score de santé', 'About the health score')}
            </summary>
            <p className="rounded-[10px] border border-line-1 bg-bg-2 p-3 text-[12px] leading-5 text-text-2">
              {t(
                'Pour les reels publiés sur la période, âgés d’au moins 72 h et mesurés depuis moins de 48 h : chaque reel vaut 100 points à partir de 1 000 vues, 30 de 300 à 999, sinon 0. La qualité est la moyenne des reels de chaque compte, puis des comptes évaluables à poids égal. Score = 90 % de cette qualité − pénalité moyenne + la moitié de l’évolution des vues en %. Une chute confirmée d’au moins 70 % de la médiane des 3 derniers reels par rapport aux 3 à 10 précédents, comparés au même âge (72–96 h), retire 20 points au compte concerné. Les vues comparent deux périodes complètes de même durée en UTC, selon la sélection (30 jours pour Tout l’historique). Le score est arrondi entre 0 et 100. Sans reels mesurés : non évaluable. Sans tendance comparable : ajustement neutre.',
                'For reels published in the selected period, at least 72 hours old and measured within the past 48 hours: each reel earns 100 points from 1,000 views, 30 from 300 to 999, otherwise 0. Quality averages reels within each account, then gives each evaluable account equal weight. Score = 90% of this quality − average penalty + half the views change in %. A confirmed drop of at least 70% in the median of the latest 3 reels versus the previous 3 to 10, compared at the same age (72–96 hours), subtracts 20 points from that account. Views compare two complete UTC periods of the selected duration (30 days for All time). The score is rounded and limited to 0–100. No measured reels: not enough data. No comparable trend: neutral adjustment.',
              )}
            </p>
          </details>
        }
      />
      {perf.isError ? (
        <ErrorInline error={perf.error} onRetry={() => void perf.refetch()} />
      ) : (
        <Tile padded>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[13px] font-medium text-text-2">
                {t('Détails par identité', 'Details by identity')}
              </div>
              <p className="mt-1 text-[11px] text-text-3">
                {t(
                  'Barres comparées à l’identité en tête du classement.',
                  'Bars are relative to the highest-ranked identity.',
                )}
              </p>
            </div>
            <div className="flex gap-1">
              <ToggleBtn active={breakdown === 'views'} onClick={() => setBreakdown('views')}>
                {t('Vues gagnées', 'Views gained')}
              </ToggleBtn>
              <ToggleBtn
                active={breakdown === 'followers'}
                onClick={() => setBreakdown('followers')}
              >
                {t('Abonnés', 'Followers')}
              </ToggleBtn>
            </div>
          </div>
          {perf.isLoading && !perf.data ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} className="h-44 w-full rounded-[10px]" />
              ))}
            </div>
          ) : sorted.length === 0 ? (
            <AnalyticsEmpty
              title={t('Aucune identité', 'No identities')}
              description={t(
                'Les groupes de comptes apparaîtront ici dès qu’ils seront disponibles.',
                'Account groups will appear here when available.',
              )}
            />
          ) : (
            <ul
              aria-label={t('Détails par identité', 'Details by identity')}
              className="grid min-w-0 gap-3 sm:grid-cols-2"
            >
              {sorted.map((row, index) => {
                const value = breakdown === 'views' ? row.totalViewsDelta : row.totalFollowers;
                return (
                  <li
                    key={row.identityId}
                    className="min-w-0 rounded-[10px] border border-line-1 p-3 [container-type:inline-size]"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-start gap-2">
                        <span
                          aria-hidden="true"
                          className="pt-0.5 font-mono text-[10px] text-text-3"
                        >
                          {index + 1}
                        </span>
                        <h4 className="min-w-0 text-[13px] font-medium leading-5 text-text-1 [overflow-wrap:anywhere]">
                          {row.name}
                        </h4>
                      </div>
                      <span aria-label={t('Score de santé', 'Health score')} className="shrink-0">
                        <IdentityHealthPopover
                          name={row.name}
                          score={row.healthScore}
                          details={row.healthDetails}
                        >
                          <Chip
                            tone={
                              row.healthScore === null ? 'default' : healthTone(row.healthScore)
                            }
                            className="shrink-0 tabular-nums"
                          >
                            <span>{t('Santé', 'Health')}</span>
                            {row.healthScore === null
                              ? t('Non évaluable', 'Not enough data')
                              : `${row.healthScore}/100`}
                          </Chip>
                        </IdentityHealthPopover>
                      </span>
                    </div>
                    <div className="mt-3">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-[11px] text-text-3">{metricLabel}</span>
                        <span className="shrink-0 text-[18px] font-semibold tabular-nums text-text-1">
                          {formatNumber(value)}
                        </span>
                      </div>
                      <div
                        aria-hidden="true"
                        className="mt-2 h-1.5 overflow-hidden rounded-full bg-bg-3"
                      >
                        <div
                          className={cn(
                            'h-full rounded-full',
                            breakdown === 'views' ? 'bg-emerald' : 'bg-violet',
                          )}
                          style={{
                            width: `${maxValue > 0 ? Math.max(0, value / maxValue) * 100 : 0}%`,
                          }}
                        />
                      </div>
                    </div>
                    <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-line-1 pt-3 [@container(min-width:360px)]:grid-cols-4">
                      <div className="min-w-0 [overflow-wrap:anywhere]">
                        <dt className="text-[11px] text-text-3">
                          {t('Actifs / total', 'Active / total')}
                        </dt>
                        <dd className="mt-1 text-[13px] font-medium tabular-nums text-text-1">
                          {row.activeAccountCount}/{row.accountCount}
                        </dd>
                      </div>
                      <div className="min-w-0 [overflow-wrap:anywhere]">
                        <dt className="text-[11px] text-text-3">
                          {t('Vues gagnées', 'Views gained')}
                        </dt>
                        <dd className="mt-1 text-[13px] font-medium tabular-nums text-text-1">
                          {formatNumber(row.totalViewsDelta)}
                        </dd>
                      </div>
                      <div className="min-w-0 [overflow-wrap:anywhere]">
                        <dt className="text-[11px] text-text-3">
                          {t('Reels / compte', 'Reels / account')}
                        </dt>
                        <dd className="mt-1 text-[13px] font-medium tabular-nums text-text-1">
                          {row.cadenceReelsPerWeek === null
                            ? '—'
                            : `${row.cadenceReelsPerWeek.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}/${t('sem', 'wk')}`}
                        </dd>
                      </div>
                      <div className="min-w-0 [overflow-wrap:anywhere]">
                        <dt className="text-[11px] text-text-3">
                          {t('Croiss. abonnés', 'Follower growth')}
                        </dt>
                        <dd
                          className={cn(
                            'mt-1 text-[13px] font-medium tabular-nums',
                            row.avgFollowerGrowthPct === null
                              ? 'text-text-4'
                              : growthTone(row.avgFollowerGrowthPct),
                          )}
                        >
                          {row.avgFollowerGrowthPct === null
                            ? '—'
                            : formatPct(row.avgFollowerGrowthPct)}
                        </dd>
                      </div>
                    </dl>
                  </li>
                );
              })}
            </ul>
          )}
        </Tile>
      )}
    </section>
  );
}

function ToggleBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'min-h-10 rounded-[8px] border px-2.5 py-1 text-[11px] font-medium transition-colors sm:min-h-0',
        active
          ? 'border-violet/40 bg-violet-soft text-violet-deep'
          : 'border-line-1 bg-bg-1 text-text-3 hover:bg-bg-2',
      )}
    >
      {children}
    </button>
  );
}

function healthTone(score: number): 'emerald' | 'amber' | 'rose' {
  if (score >= 70) return 'emerald';
  if (score >= 40) return 'amber';
  return 'rose';
}

function growthTone(pct: number): string {
  if (pct > 0) return 'text-emerald';
  if (pct < 0) return 'text-rose';
  return 'text-text-3';
}
